package ai.wagemix.analytics

import ai.wagemix.logic.analytics.WMScreenView
import ai.wagemix.logic.analytics.WMSignInMethod
import co.touchlab.kermit.Severity
import com.fsryan.foundation.util.log.FSLog
import com.fsryan.foundation.util.log.sendEventInclusive

/*
 * The analytics events the site itself emits (planning/analytics-plan.md §5): screen views, the marketing calls to
 * action, and the identity and login-session events. Names are "WM" plus the PascalCase of the catalogue name;
 * WMAnalyticsGate forwards them under the snake_case name and adds the session and screen context.
 */

/** A marketing call to action (`cta_clicked.cta`). */
internal enum class WMCta { StartMixing, ViewPlans, CreateAccount, OpenApp }

/** Where a marketing call to action sits (`cta_clicked.location`). */
internal enum class WMCtaLocation { Hero, CtaBand, Header, SharedMix }

private inline fun FSLog.sendWMSiteEvent(
    eventName: String,
    crossinline fields: MutableMap<String, Any>.() -> Unit = {},
) {
    sendEventInclusive(severity = Severity.Info, eventName = eventName) {
        fields()
    }
}

/**
 * [origin] is the page's origin (`https://wagemix.ai`): OpenPanel parses `__path` as a full URL into its origin and
 * path columns, and a bare pattern (`/mix/[id]`) parses to an empty path, which leaves the Pages report empty. The
 * pattern keeps ids and query strings out either way.
 */
internal fun FSLog.sendWMScreenView(screenView: WMScreenView, title: String, origin: String) {
    sendWMSiteEvent(eventName = "WMScreenView") {
        put("__path", origin + screenView.pattern)
        put("__title", title)
        screenView.referrer?.let { put("__referrer", it) }
        put("screen", screenView.screen)
        screenView.previousScreen?.let { put("previous_screen", it) }
        screenView.msOnPreviousScreen?.let { put("ms_on_previous_screen", it) }
        screenView.utm.source?.let { put("utm_source", it) }
        screenView.utm.medium?.let { put("utm_medium", it) }
        screenView.utm.campaign?.let { put("utm_campaign", it) }
        screenView.utm.content?.let { put("utm_content", it) }
        screenView.utm.term?.let { put("utm_term", it) }
        screenView.entityId?.let { put("entity_id", it) }
        screenView.authActionMode?.let { put("auth_action_mode", it) }
        put("is_entry", screenView.isEntry)
    }
}

internal fun FSLog.sendWMCtaClicked(cta: WMCta, location: WMCtaLocation) {
    sendWMSiteEvent(eventName = "WMCtaClicked") {
        put(
            "cta",
            when (cta) {
                WMCta.StartMixing -> "start_mixing"
                WMCta.ViewPlans -> "view_plans"
                WMCta.CreateAccount -> "create_account"
                WMCta.OpenApp -> "open_app"
            }
        )
        put(
            "location",
            when (location) {
                WMCtaLocation.Hero -> "hero"
                WMCtaLocation.CtaBand -> "cta_band"
                WMCtaLocation.Header -> "header"
                WMCtaLocation.SharedMix -> "shared_mix"
            }
        )
    }
}

internal fun FSLog.sendWMUserIdentified(method: WMSignInMethod, isNewUser: Boolean) {
    sendWMSiteEvent(eventName = "WMUserIdentified") {
        put("method", method.stringValue)
        put("is_new_user", isNewUser)
    }
}

internal fun FSLog.sendWMLoginSessionStarted(restored: Boolean) {
    sendWMSiteEvent(eventName = "WMLoginSessionStarted") {
        put("restored", restored)
    }
}

internal fun FSLog.sendWMSignedOut(loginSessionMinutes: Long) {
    sendWMSiteEvent(eventName = "WMSignedOut") {
        put("login_session_minutes", loginSessionMinutes)
    }
}
